import { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Plus, 
  Edit2, 
  Trash2, 
  FolderOpen,
  FileText,
  Search,
  ChevronRight,
  Save,
  X,
  Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  getCategories, 
  createCategory, 
  updateCategory, 
  deleteCategory,
  getArticles,
  createArticle,
  updateArticle,
  deleteArticle
} from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export const KnowledgeBasePage = () => {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [categoryDialog, setCategoryDialog] = useState({ open: false, data: null });
  const [articleDialog, setArticleDialog] = useState({ open: false, data: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', icon: 'BookOpen' });
  const [articleForm, setArticleForm] = useState({ 
    title: '', 
    content: '', 
    category_id: '', 
    keywords: [],
    is_active: true 
  });
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadArticles(selectedCategory.id);
    } else {
      loadArticles();
    }
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, artRes] = await Promise.all([
        getCategories(),
        getArticles()
      ]);
      setCategories(catRes.data);
      setArticles(artRes.data);
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async (categoryId = null) => {
    try {
      const res = await getArticles(categoryId);
      setArticles(res.data);
    } catch (error) {
      // Production: error logged silently
    }
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        icon: category.icon || 'BookOpen'
      });
    } else {
      setCategoryForm({ name: '', description: '', icon: 'BookOpen' });
    }
    setCategoryDialog({ open: true, data: category });
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      if (categoryDialog.data) {
        await updateCategory(categoryDialog.data.id, categoryForm);
        toast.success('Category updated');
      } else {
        await createCategory(categoryForm);
        toast.success('Category created');
      }
      setCategoryDialog({ open: false, data: null });
      loadData();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  // Article handlers
  const openArticleDialog = (article = null) => {
    if (article) {
      setArticleForm({
        title: article.title,
        content: article.content,
        category_id: article.category_id,
        keywords: article.keywords || [],
        is_active: article.is_active
      });
    } else {
      setArticleForm({
        title: '',
        content: '',
        category_id: selectedCategory?.id || (categories[0]?.id || ''),
        keywords: [],
        is_active: true
      });
    }
    setArticleDialog({ open: true, data: article });
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title.trim() || !articleForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    if (!articleForm.category_id) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      if (articleDialog.data) {
        await updateArticle(articleDialog.data.id, articleForm);
        toast.success('Article updated');
      } else {
        await createArticle(articleForm);
        toast.success('Article created');
      }
      setArticleDialog({ open: false, data: null });
      loadArticles(selectedCategory?.id);
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !articleForm.keywords.includes(keywordInput.trim())) {
      setArticleForm(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setArticleForm(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  // Delete handlers
  const handleDelete = async () => {
    try {
      if (deleteDialog.type === 'category') {
        await deleteCategory(deleteDialog.id);
        toast.success('Category deleted');
        setSelectedCategory(null);
      } else {
        await deleteArticle(deleteDialog.id);
        toast.success('Article deleted');
        setSelectedArticle(null);
      }
      setDeleteDialog({ open: false, type: null, id: null });
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredArticles = articles.filter(art => 
    art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    art.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    art.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getCategoryName = (categoryId) => {
    return categories.find(c => c.id === categoryId)?.name || 'Unknown';
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="knowledge-base-page">
      {/* Categories Sidebar */}
      <div className="w-64 border-r border-border flex flex-col bg-card/30">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Categories</h2>
            <Button 
              size="icon" 
              variant="ghost"
              onClick={() => openCategoryDialog()}
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            <div
              className={cn(
                "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors",
                "hover:bg-muted/30",
                !selectedCategory && "bg-primary/10 border-l-2 border-l-primary"
              )}
              onClick={() => setSelectedCategory(null)}
              data-testid="all-categories"
            >
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">All Articles</span>
              <Badge variant="secondary" className="ml-auto text-xs">
                {articles.length}
              </Badge>
            </div>
            
            {categories.map((cat) => (
              <div
                key={cat.id}
                className={cn(
                  "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors group",
                  "hover:bg-muted/30",
                  selectedCategory?.id === cat.id && "bg-primary/10 border-l-2 border-l-primary"
                )}
                onClick={() => setSelectedCategory(cat)}
                data-testid={`category-${cat.id}`}
              >
                <FolderOpen className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium flex-1 truncate">{cat.name}</span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCategoryDialog(cat);
                    }}
                  >
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteDialog({ open: true, type: 'category', id: cat.id });
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Articles List */}
      <div className="flex-1 flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-background/50"
                data-testid="search-articles"
              />
            </div>
            <Button 
              onClick={() => openArticleDialog()}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="add-article-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Article
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-24 skeleton rounded-lg" />
                ))}
              </div>
            ) : filteredArticles.length > 0 ? (
              <div className="space-y-3">
                {filteredArticles.map((article) => (
                  <Card 
                    key={article.id}
                    className={cn(
                      "bg-card/50 border-border/60 cursor-pointer transition-all hover:border-primary/30",
                      selectedArticle?.id === article.id && "border-primary"
                    )}
                    onClick={() => setSelectedArticle(article)}
                    data-testid={`article-${article.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                            <h3 className="font-semibold truncate">{article.title}</h3>
                            {!article.is_active && (
                              <Badge variant="secondary" className="text-xs">Draft</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {article.content}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {getCategoryName(article.category_id)}
                            </Badge>
                            {article.keywords.slice(0, 3).map((kw) => (
                              <Badge key={kw} className="bg-primary/10 text-primary text-xs">
                                {kw}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              openArticleDialog(article);
                            }}
                            data-testid={`edit-article-${article.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteDialog({ open: true, type: 'article', id: article.id });
                            }}
                            data-testid={`delete-article-${article.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No articles found</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Create your first article to get started
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialog.open} onOpenChange={(open) => setCategoryDialog({ open, data: null })}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>
              {categoryDialog.data ? 'Edit Category' : 'New Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Setup Guide"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this category"
                rows={3}
                data-testid="category-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog({ open: false, data: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} className="bg-primary text-primary-foreground" data-testid="save-category-btn">
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog open={articleDialog.open} onOpenChange={(open) => setArticleDialog({ open, data: null })}>
        <DialogContent className="max-w-2xl" data-testid="article-dialog">
          <DialogHeader>
            <DialogTitle>
              {articleDialog.data ? 'Edit Article' : 'New Article'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input
                value={articleForm.title}
                onChange={(e) => setArticleForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Article title"
                data-testid="article-title-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select
                value={articleForm.category_id}
                onValueChange={(value) => setArticleForm(prev => ({ ...prev, category_id: value }))}
              >
                <SelectTrigger data-testid="article-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Content</label>
              <Textarea
                value={articleForm.content}
                onChange={(e) => setArticleForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Article content..."
                rows={10}
                className="font-mono text-sm"
                data-testid="article-content-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Keywords (for AI matching)</label>
              <div className="flex gap-2">
                <Input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  placeholder="Add keyword"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                  data-testid="keyword-input"
                />
                <Button type="button" variant="secondary" onClick={handleAddKeyword}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {articleForm.keywords.map((kw) => (
                  <Badge key={kw} className="bg-primary/10 text-primary gap-1">
                    <Tag className="w-3 h-3" />
                    {kw}
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(kw)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleDialog({ open: false, data: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveArticle} className="bg-primary text-primary-foreground" data-testid="save-article-btn">
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, type: null, id: null })}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.type === 'category' 
                ? 'This will delete the category and all its articles. This action cannot be undone.'
                : 'This will permanently delete the article. This action cannot be undone.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
